import { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, TrendingDown } from "lucide-react";
import AdminShell, { Body } from "@/components/admin/AdminShell";
import { Card } from "@/components/Common";
import { num, useAdminGet } from "@/lib/admin";
import { cn } from "@/lib/utils";

export const FUNNEL = [
  ["visitor", "Website visitor", "Attract job seekers"], ["registration", "Registration", "Create free account"],
  ["resume_upload", "Resume upload", "Get user invested"], ["ai_score", "AI resume score", "Demonstrate value"],
  ["companion", "Career Companion", "Establish engagement"], ["elite_trial", "Elite trial", "Demonstrate premium features"],
  ["paid", "Paid conversion", "Generate revenue"], ["retention", "Retention", "Keep subscribers"], ["referral", "Referral", "Acquire additional users"],
];

export const buildFunnel = (stages = []) => {
  const by = Object.fromEntries(stages.map((s) => [s.stage, s]));
  const rows = FUNNEL.map(([id, name, goal], i) => {
    const s = by[id] || {}, prev = i ? by[FUNNEL[i - 1][0]]?.count : null, has = s.count != null;
    const conv = !has || !i ? null : s.conversion_from_prev ?? (prev ? (100 * s.count) / prev : null);
    return { id, name, goal, count: s.count, conv, drop: conv == null ? null : Math.max(0, 100 - conv), lost: has && i && prev != null ? Math.max(0, prev - s.count) : null };
  });
  const worst = rows.slice(1).filter((r) => r.drop != null).sort((a, b) => b.drop - a.drop)[0];
  return { rows, worst: worst?.id };
};

const Row = ({ r, max, worst }) => (
  <div className={cn("grid grid-cols-[1fr_auto] items-center gap-x-4 gap-y-1.5 rounded-xl border p-3.5 sm:grid-cols-[220px_1fr_150px]", worst ? "border-red-500/50 bg-red-500/10" : "border-[#27272A] bg-[#121214]")} data-testid={`gf-stage-${r.id}`}>
    <div><div className="text-sm text-zinc-100">{r.name}</div><div className="text-[11px] text-zinc-500">{r.goal}</div></div>
    <div className="order-3 col-span-2 h-2.5 overflow-hidden rounded-full bg-[#27272A] sm:order-none sm:col-span-1"><div className={cn("h-full rounded-full", worst ? "bg-red-400" : "bg-gradient-to-r from-[#D4AF37] to-[#F3C649]")} style={{ width: `${(100 * (r.count || 0)) / max}%` }} /></div>
    <div className="text-right">
      <div className="font-mono text-sm text-zinc-100" data-testid={`gf-count-${r.id}`}>{num(r.count)}</div>
      {r.conv != null && <div className={cn("text-[11px]", worst ? "font-semibold text-red-300" : "text-zinc-500")} data-testid={`gf-conv-${r.id}`}>{Math.round(r.conv)}% from previous · −{num(r.lost)}</div>}
    </div>
  </div>
);

export default function GrowthFunnel() {
  const [days, setDays] = useState(30);
  const q = useAdminGet(`/admin/cc/growth-funnel?days=${days}`);
  const actions = [7, 30, 90].map((n) => (
    <button key={n} onClick={() => setDays(n)} data-testid={`gf-days-${n}`} className={cn("rounded-full px-3 py-1 text-xs", days === n ? "bg-[#D4AF37] font-semibold text-black" : "border border-[#27272A] text-zinc-400 hover:text-zinc-100")}>{n} days</button>
  ));
  return (
    <AdminShell title="Growth Funnel" subtitle="From website visitor to referral: see exactly where people drop out." actions={actions}>
      <Body q={q}>{(d) => {
        const { rows, worst } = buildFunnel(d.stages);
        const w = rows.find((r) => r.id === worst);
        const max = Math.max(1, ...rows.map((r) => r.count || 0));
        return (
          <>
            {w && (
              <Card className="mb-6 border-red-500/40 bg-red-500/10" data-testid="gf-biggest-drop">
                <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-red-300"><TrendingDown className="h-4 w-4" />Biggest drop-off</div>
                <p className="mt-1 font-display text-xl text-zinc-50">{rows[rows.indexOf(w) - 1].name} → {w.name}: {Math.round(w.drop)}% drop out</p>
                <p className="mt-1 text-sm text-zinc-300">{num(w.lost)} people stopped here in the last {d.period_days || days} days.{d.insight ? ` ${d.insight}` : ""}</p>
                <Link to="/admin/cc/advisor" state={{ prompt: `In the last ${d.period_days || days} days, ${Math.round(w.drop)}% of people dropped out between "${rows[rows.indexOf(w) - 1].name}" and "${w.name}" (${num(w.lost)} people). Give me a step-by-step plan to fix this drop-off, with quick wins for this week and how to measure success.` }}
                  className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#D4AF37] px-4 py-2 text-xs font-semibold text-black transition-colors hover:bg-[#F3C649]" data-testid="gf-ask-advisor"><Sparkles className="h-3.5 w-3.5" />Ask the Advisor for a fix plan</Link>
              </Card>
            )}
            <div className="space-y-2" data-testid="gf-stages">{rows.map((r) => <Row key={r.id} r={r} max={max} worst={r.id === worst} />)}</div>
          </>
        );
      }}</Body>
    </AdminShell>
  );
}
